import { gql } from '@apollo/client';
import { ElementType, useContext } from 'react';
import { Case, Default, Switch, When } from 'react-if';

import { CreditFactorTradeline } from '@generated-gql/generated-types';
import classes from '@nerdwallet/base-styles/classes';
import { colors } from '@nerdwallet/base-styles/variables';
import { Text } from '@nerdwallet/currency/components/Text';
import GenericAccountIcon from '@nerdwallet/react-icon/material/AccountBalance';
import RightCaretIcon from '@nerdwallet/react-icon/material/KeyboardArrowRight';

import ListItem, { DENSITY } from '@components/list-item/list-item';
import RoundIconContainer from '@components/round-icon-container';
import CurrentDateContext from '@lib/contexts/current-date-context';
import { isTradelineOpen } from '@lib/utils/credit-score';

interface Component<E> extends React.FC<E> {
  fragment: any;
}

export interface TradelineItemProps {
  fromKey?: string;
  Icon?: ElementType; // React component
  RightHandComponent?: ElementType; // React component
  SubtitleComponent?: ElementType; // React component
  tradeline: CreditFactorTradeline;
}

const TradelineItem: Component<TradelineItemProps> = ({
  Icon,
  tradeline,
  RightHandComponent,
  SubtitleComponent,
}) => {
  const isOpen = isTradelineOpen(tradeline);
  const displayName =
    tradeline?.tradelineDisplayName || tradeline?.creditorName;

  const { currentDate } = useContext(CurrentDateContext);

  return (
    <li key={tradeline?.compositeKey} className={classes('width-100')}>
      <ListItem
        density={DENSITY.COMFORTABLE}
        icon={
          <Switch>
            <Case condition={!!Icon}>
              <Icon tradeline={tradeline} textAligned />
            </Case>
            <Default>
              <RoundIconContainer
                color={
                  isOpen ? colors['blue-lightest'] : colors['neutral-lightest']
                }
                size={32}
              >
                <GenericAccountIcon
                  color={
                    isOpen ? colors['blue-light'] : colors['neutral-light']
                  }
                />
              </RoundIconContainer>
            </Default>
          </Switch>
        }
        amount={
          <When condition={!!RightHandComponent}>
            <RightHandComponent
              tradeline={tradeline}
              effectiveAppDate={currentDate}
            />
          </When>
        }
        rightIcon={<RightCaretIcon color={colors['neutral-darker']} />}
        title={
          <Switch>
            <Case condition={isOpen}>{displayName}</Case>
            <Default>
              <Text tone="light">{displayName}</Text>
            </Default>
          </Switch>
        }
        subtitle={
          <When condition={!!SubtitleComponent}>
            <SubtitleComponent tradeline={tradeline} />
          </When>
        }
      />
    </li>
  );
};
TradelineItem.fragment = gql`
  fragment tradelineItemFragment on CreditFactorTradeline {
    tradelineDisplayName
    creditorName
    compositeKey
    ...isTradelineOpenFragment
  }
  ${isTradelineOpen.fragment}
`;

export default TradelineItem;
